// Updated API service with automation endpoints

import axios from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5120';

const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests. When "Remember me" is unchecked the token lives
// in sessionStorage (cleared when the browser closes); otherwise localStorage.
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// An expired or rejected session used to fail silently. Tokens last 7 days; when
// one lapsed, every call came back 401, each caller logged to the console and set
// nothing, and the app sat there fully rendered and completely empty. Nothing told
// anyone why, so the workaround people found on their own was to log out and back
// in — which is exactly what a session expiring is supposed to prompt, except they
// had to guess it.
//
// Now a 401 clears the dead session and announces it. AuthContext listens and drops
// to the login screen, so the app says "signed out" instead of showing blank lists.
//
// The login call is exempt: a failed sign-in is a 401 about the credentials just
// typed, not about a stale session, and clearing storage there would fight the form.
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    const isAuthAttempt = url.includes('/auth/');

    if (status === 401 && !isAuthAttempt) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      window.dispatchEvent(new CustomEvent('auth:expired'));
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => apiClient.post('/auth/login', credentials),
  register: (userData) => apiClient.post('/auth/register', userData),
  forgotPassword: (email) => apiClient.post('/auth/forgot-password', { email }),
  resetPassword: (data) => apiClient.post('/auth/reset-password', data),
};

// User API

// Custom fields — admin-defined extras on a Project or Contact.
export const customFieldAPI = {
  // Definitions. Active only by default; each carries a `visibility` of
  // Detail | Intake | Hidden, which the UI honours and the API ignores.
  getDefinitions: (entity = 'Project') =>
    apiClient.get(`/custom-fields?entity=${encodeURIComponent(entity)}`),
  getBuiltIn: (entity = 'Project') =>
    apiClient.get(`/custom-fields/built-in?entity=${encodeURIComponent(entity)}`),
  // Admin only. Options is a list of choices; an empty list clears the override.
  setBuiltInOptions: (entity, fieldName, options) =>
    apiClient.put(
      `/custom-fields/built-in/${encodeURIComponent(fieldName)}?entity=${encodeURIComponent(entity)}`,
      { options }
    ),
  getValues: (entity, entityId) =>
    apiClient.get(`/custom-fields/values/${entity}/${entityId}`),
  // Takes [{ definitionId, value }] and returns the whole set back, so a save
  // cannot leave the caller showing something different from what is stored.
  saveValues: (entity, entityId, items) =>
    apiClient.put(`/custom-fields/values/${entity}/${entityId}`, items),
};
// Machine credentials for integrations. Admin-only server-side; the UI hides the
// section for non-admins so nobody is offered a button that can only 403.
export const apiKeyAPI = {
  getAll: () => apiClient.get('/apikeys'),
  // The response to this is the ONLY time the key is readable — it is stored hashed,
  // so there is no endpoint that can return it again.
  // `options` carries the key's identity choice: { createServiceUser: true } (the
  // default server-side) or { linkedUserId, createServiceUser: false } to write as an
  // existing user. A key with no identity cannot author notes, tasks or documents.
  create: (name, options = {}) => apiClient.post('/apikeys', { name, ...options }),
  revoke: (id) => apiClient.delete(`/apikeys/${id}`),
  // Repoint an existing key at a different user, or null to unlink.
  setLinkedUser: (id, userId) => apiClient.put(`/apikeys/${id}/linked-user`, { userId }),
  // For a key that predates linked identities: there is no service account to pick
  // from a list yet, and the alternative is an integration writing under a real
  // person's name.
  createServiceUserFor: (id) =>
    apiClient.put(`/apikeys/${id}/linked-user`, { createServiceUser: true }),
};

export const userAPI = {
  // Service accounts (an integration's author row) are excluded server-side unless
  // asked for — they are not people and must not appear in assignee pickers.
  getAll: (includeServiceAccounts = false) =>
    apiClient.get(includeServiceAccounts ? '/users?includeServiceAccounts=true' : '/users'),
  // Create a staff account. Hits /auth/register, which requires Admin now that
  // self-registration is closed. Lives here rather than on authAPI because it is an
  // admin action on someone else's account, not a sign-up — and deliberately does
  // not touch the caller's session, which is what AuthContext.register used to do.
  create: (data) => apiClient.post('/auth/register', data),
  getById: (id) => apiClient.get(`/users/${id}`),
  getActivity: (id) => apiClient.get(`/users/${id}/activity`),
  updateMyProfile: (data) => apiClient.put('/users/me', data),
  updateRole: (id, role) => apiClient.put(`/users/${id}/role`, { role }),
  delete: (id) => apiClient.delete(`/users/${id}`),
};

// Customer API
// getAll(options): { search, type, limit, includeProjects }
// Legacy: if a string is passed, it's treated as the search term.
export const customerAPI = {
  getAll: (options = {}) => {
    const params = typeof options === 'string' ? { search: options } : options;
    const filtered = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    );
    const qs = new URLSearchParams(filtered).toString();
    return apiClient.get(`/customers${qs ? `?${qs}` : ''}`);
  },
  getById: (id) => apiClient.get(`/customers/${id}`),
  create: (customer) => apiClient.post('/customers', customer),
  update: (id, customer) => apiClient.put(`/customers/${id}`, customer),
  delete: (id) => apiClient.delete(`/customers/${id}`),
};

// Project API
export const projectAPI = {
  getAll: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.search) qs.set('search', params.search);
    if (params.statuses && params.statuses.length) qs.set('statuses', params.statuses.join(','));
    if (params.sortBy) qs.set('sortBy', params.sortBy);
    if (params.sortDir) qs.set('sortDir', params.sortDir);
    const q = qs.toString();
    return apiClient.get(`/projects${q ? `?${q}` : ''}`);
  },
  getById: (id) => apiClient.get(`/projects/${id}`),
  getByCustomer: (customerId) => apiClient.get(`/projects/customer/${customerId}`),
  create: (project) => apiClient.post('/projects', project),
  update: (id, project) => apiClient.put(`/projects/${id}`, project),
  updateStatus: (id, status) => apiClient.patch(`/projects/${id}/status`, { status }),
  // Per-section crewing. setCrewAssignments replaces the whole set, so send every
  // section that should survive, not just the changed ones.
  getCrewAssignments: (id) => apiClient.get(`/projects/${id}/crews`),
  setCrewAssignments: (id, assignments) => apiClient.put(`/projects/${id}/crews`, assignments),
  delete: (id) => apiClient.delete(`/projects/${id}`),
  refireAutomations: (id) => apiClient.post(`/projects/${id}/refire-automations`),
};

// Task API
export const taskAPI = {
  getAll: () => apiClient.get('/tasks'),
  getById: (id) => apiClient.get(`/tasks/${id}`),
  create: (task) => apiClient.post('/tasks', task),
  update: (id, task) => apiClient.put(`/tasks/${id}`, task),
  delete: (id) => apiClient.delete(`/tasks/${id}`),
  updateStatus: (id, status) => apiClient.patch(`/tasks/${id}/status`, { status }),
  getByProject: (projectId) => apiClient.get(`/tasks/project/${projectId}`),
  cleanup: (daysOld = 30) => apiClient.delete(`/tasks/cleanup?daysOld=${daysOld}`),
};

// Automation API
export const automationAPI = {
  getAll: () => apiClient.get('/automations'),
  getById: (id) => apiClient.get(`/automations/${id}`),
  create: (automation) => apiClient.post('/automations', automation),
  update: (id, automation) => apiClient.put(`/automations/${id}`, automation),
  delete: (id) => apiClient.delete(`/automations/${id}`),
  toggle: (id, isActive) => apiClient.patch(`/automations/${id}/toggle`, { isActive }),
};

// Document API
export const documentAPI = {
  getByProject: (projectId) => apiClient.get(`/documents/project/${projectId}`),
  getUploadUrl: (data) => apiClient.post('/documents/upload-url', data),
  saveMetadata: (doc) => apiClient.post('/documents', doc),
  getDownloadUrl: (id) => apiClient.get(`/documents/${id}/download-url`),
  getHtml: (id) => apiClient.get(`/documents/${id}/html`),
  getContent: (id) => apiClient.get(`/documents/${id}/content`, { responseType: 'arraybuffer' }),
  updatePdf: (id, blob) => apiClient.put(`/documents/${id}/pdf`, blob, { headers: { 'Content-Type': 'application/pdf' } }),
  updateHtml: (id, html) => apiClient.put(`/documents/${id}/html`, { html }),
  delete: (id) => apiClient.delete(`/documents/${id}`),
};

// Activity Log API
export const activityLogAPI = {
  getByProject: (projectId, type) => apiClient.get(`/activitylogs/project/${projectId}${type ? `?type=${type}` : ''}`),
  delete: (id) => apiClient.delete(`/activitylogs/${id}`),
};

// Note API
export const noteAPI = {
  create: (data) => apiClient.post('/notes', data),
};

// Notification API
export const notificationAPI = {
  getAll: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', params.page);
    if (params.pageSize) qs.set('pageSize', params.pageSize);
    const q = qs.toString();
    return apiClient.get(`/notifications${q ? `?${q}` : ''}`);
  },
  getUnreadCount: () => apiClient.get('/notifications/unread-count'),
  markRead: (id) => apiClient.put(`/notifications/${id}/read`),
  markAllRead: () => apiClient.put('/notifications/read-all'),
  clearAll: () => apiClient.delete('/notifications/clear-all'),
};

// Google Auth API
export const googleAuthAPI = {
  getAuthUrl: () => apiClient.get('/google/auth-url'),
  callback: (code, state) => apiClient.get(`/google/callback?code=${code}&state=${state}`),
  getStatus: () => apiClient.get('/google/status'),
  getConnectedAccounts: () => apiClient.get('/google/connected-accounts'),
  disconnectAccount: (id) => apiClient.delete(`/google/connected-accounts/${id}`),
  updateAccount: (id, data) => apiClient.patch(`/google/connected-accounts/${id}`, data),
};

// Email API
export const emailAPI = {
  send: (data) => apiClient.post('/email/send', data),
  sendTemplate: (data) => apiClient.post('/email/send-template', data),
  getHistory: (projectId) => apiClient.get(`/email/history/${projectId}`),
  getSendAsOptions: () => apiClient.get('/email/send-as-options'),
  getThread: (logId) => apiClient.get(`/email/thread/${logId}`),
  downloadAttachment: (logId, { messageId, attachmentId, filename }) =>
    apiClient.get(`/email/attachment/${logId}`, {
      params: { messageId, attachmentId, filename },
      responseType: 'blob',
    }),
};

// SMS / texting API (Twilio-backed two-way inbox)
export const smsAPI = {
  getConversations: () => apiClient.get('/sms/conversations'),
  getConversation: (phone) => apiClient.get('/sms/conversation', { params: { phone } }),
  // Flat log of texts the system sent itself (automation actions + auto-replies),
  // newest first — the "are automations actually texting?" view.
  getAutomated: (limit = 100) => apiClient.get('/sms/automated', { params: { limit } }),
  send: (data) => apiClient.post('/sms/send', data),
  // Crews and staff — the people you can text who are not contacts.
  getDirectory: (search = '') => apiClient.get('/sms/directory', { params: { search } }),
  unreadCount: () => apiClient.get('/sms/unread-count'),
  uploadMedia: (file) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post('/sms/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Project (job) contacts — additional contacts attached to a job for notification fan-out.
export const projectContactsAPI = {
  list: (projectId) => apiClient.get(`/projects/${projectId}/contacts`),
  add: (projectId, data) => apiClient.post(`/projects/${projectId}/contacts`, data),
  update: (projectId, id, data) => apiClient.put(`/projects/${projectId}/contacts/${id}`, data),
  remove: (projectId, id) => apiClient.delete(`/projects/${projectId}/contacts/${id}`),
};

// Work Orders API
export const workOrdersAPI = {
  getAll: (params = {}) => apiClient.get('/workorders', { params }),
  get: (id) => apiClient.get(`/workorders/${id}`),
  create: (data) => apiClient.post('/workorders', data),
  update: (id, data) => apiClient.put(`/workorders/${id}`, data),
  delete: (id) => apiClient.delete(`/workorders/${id}`),
};

// Per-user board layout: hidden stages, stage order, card fields. Always scoped to
// the signed-in user server-side.
export const boardPreferencesAPI = {
  get: (boardKey = 'projects') => apiClient.get('/board-preferences', { params: { boardKey } }),
  save: (prefs, boardKey = 'projects') =>
    apiClient.put('/board-preferences', prefs, { params: { boardKey } }),
  reset: (boardKey = 'projects') => apiClient.delete('/board-preferences', { params: { boardKey } }),
};

// Crews API (install teams work orders are dispatched to; not CRM users)
export const crewsAPI = {
  // activeOnly is what the project assignment dropdown wants; the admin page
  // lists everything so retired crews can still be edited.
  getAll: (activeOnly = false) => apiClient.get('/crews', { params: { activeOnly } }),
  get: (id) => apiClient.get(`/crews/${id}`),
  create: (data) => apiClient.post('/crews', data),
  update: (id, data) => apiClient.put(`/crews/${id}`, data),
  delete: (id) => apiClient.delete(`/crews/${id}`),
};

// Boards API (Job Nimbus-style multi-boards with custom columns)
export const boardsAPI = {
  getAll: () => apiClient.get('/boards'),
  get: (id) => apiClient.get(`/boards/${id}`),
  create: (data) => apiClient.post('/boards', data),
  update: (id, data) => apiClient.put(`/boards/${id}`, data),
  delete: (id) => apiClient.delete(`/boards/${id}`),
  reorder: (orderedIds) => apiClient.post('/boards/reorder', { orderedIds }),
  addColumn: (boardId, data) => apiClient.post(`/boards/${boardId}/columns`, data),
  updateColumn: (id, data) => apiClient.put(`/boards/columns/${id}`, data),
  deleteColumn: (id) => apiClient.delete(`/boards/columns/${id}`),
  reorderColumns: (boardId, orderedIds) => apiClient.post(`/boards/${boardId}/columns/reorder`, { orderedIds }),
  addCard: (boardId, columnId, entityId) => apiClient.post(`/boards/${boardId}/cards`, { columnId, entityId }),
  moveCard: (cardId, columnId, position) => apiClient.post(`/boards/cards/${cardId}/move`, { columnId, position }),
  removeCard: (cardId) => apiClient.delete(`/boards/cards/${cardId}`),
  available: (boardId, search) => apiClient.get(`/boards/${boardId}/available`, { params: { search } }),
};

// Email Templates API
export const emailTemplatesAPI = {
  getAll: () => apiClient.get('/email-templates'),
  create: (template) => apiClient.post('/email-templates', template),
  update: (id, template) => apiClient.put(`/email-templates/${id}`, template),
  delete: (id) => apiClient.delete(`/email-templates/${id}`),
};

// Document Templates API — HTML templates merged with project variables and
// rendered to PDF, then filed as a Document on the project.
export const documentTemplatesAPI = {
  getAll: () => apiClient.get('/document-templates'),
  create: (template) => apiClient.post('/document-templates', template),
  update: (id, template) => apiClient.put(`/document-templates/${id}`, template),
  delete: (id) => apiClient.delete(`/document-templates/${id}`),
  generate: (id, data) => apiClient.post(`/document-templates/${id}/generate`, data),
};

export default apiClient;